// /admin/v1/cardkeys — 卡密库存 (h5-benefit-store).
// Mirrors backend handlers/cardkey/admin.go (MountAdmin → /cardkeys*).
//
//   POST /admin/v1/cardkeys/import  — 按 SKU 批量导入 (文本粘贴, 去重报告)
//   GET  /admin/v1/cardkeys/stats   — 按 SKU 的 available/assigned/void 计数 (?app_id=)
//   GET  /admin/v1/cardkeys/:id     — 单条查看 (解密明文; 后端留痕)

import { get, list, post } from './client';
import type { ListResult } from './client';

/** model.CardKeySKUStat — GET /stats 的一行 (按 SKU 汇总). */
export interface CardKeySKUStat {
  sku_id: number;
  sku_code: string;
  sku_name: string;
  available: number;
  assigned: number;
  void: number;
  /** 服务端按阈值 (默认 10) 计算的低库存标记. */
  low_stock: boolean;
  // --- 供应商多卡种/临期可见性 (cardkey-supplier-cardapi task 5.2) ---
  /** 可用库存中 7 天内到期的卡数 (>0 = 临期告警). */
  expiring_soon: number;
  /** 可用库存中最早的有效期 (无有效期卡时缺省). */
  nearest_valid_to?: string | null;
  /** 库存含供应商 (card-api) 来源的卡. */
  has_supplier: boolean;
  /** 库存含人工导入 (manual_import) 来源的卡. */
  has_manual: boolean;
}

export function listCardKeyStats(appId: number): Promise<ListResult<CardKeySKUStat>> {
  return list<CardKeySKUStat>('/admin/v1/cardkeys/stats', { app_id: appId });
}

/**
 * cardapi.DiscListItem — GET /disclist 的一行 (card-api 授权商品 + 折后价).
 * 后端 Go 结构体无 json tag, 键名即字段名 (CardName/HCamiCode/...).
 */
export interface CardKeyDiscListItem {
  CardName: string;
  CardPrice: string;
  /** 官方卡 (HHT) 产品编码 + 折后单价. */
  HCamiCode: string;
  HCamiPrice: string;
  /** 自生成卡 (YHT) 产品编码 + 折后单价. */
  YCamiCode: string;
  YCamiPrice: string;
}

/**
 * 同步 card-api 授权商品折后价 (只读). ⚠️ 供应商侧限流 10s/次 — 仅手动触发 (按钮),
 * 切勿轮询. 未配置 card-api 时后端返回 501.
 */
export function listCardKeyDiscList(): Promise<ListResult<CardKeyDiscListItem>> {
  return list<CardKeyDiscListItem>('/admin/v1/cardkeys/disclist');
}

export interface ImportCardKeysResult {
  /** 本次新入库条数. */
  imported: number;
  /** 与库内已有 code 重复而被跳过的条数 (含粘贴文本内部重复). */
  duplicated: number;
}

export function importCardKeys(skuId: number, codesText: string): Promise<ImportCardKeysResult> {
  return post<ImportCardKeysResult>('/admin/v1/cardkeys/import', {
    sku_id: skuId,
    codes_text: codesText,
  });
}

/** GET /:id 单条明文 — admin 侧唯一明文出口, 后端每次调用都留痕. */
export interface CardKeyDetail {
  id: number;
  sku_id: number;
  batch_id: string;
  status: 'available' | 'assigned' | 'void';
  /** 解密后的明文卡密. */
  code: string;
  order_id?: string | null;
  fulfillment_id?: string | null;
  assigned_at?: string | null;
  created_at?: string;
}

/**
 * operator = 当前登录管理员用户名 (后端审计日志用, 空默认 "admin").
 * 注意: 每次调用后端都会记录留痕 — 仅在确有需要时查看明文.
 */
export function getCardKey(id: number, operator?: string): Promise<CardKeyDetail> {
  return get<CardKeyDetail>(`/admin/v1/cardkeys/${id}`, operator ? { operator } : undefined);
}
